import React, { useEffect, useRef, useState } from "react";
import { useLang, useT } from "@/lib/i18n";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUp } from "lucide-react";

interface FinalCtaSectionProps {
  onContactClick?: () => void;
  onExploreProducts?: () => void;
  onDiagnosisSubmit?: (brandName: string) => void;
  isNavigating?: boolean;
}

const TYPEWRITER_LINES_ZH = [
  "输入品牌名称，五分钟获取诊断报告…",
  "「新锐宠物粮品牌，想打小红书秋季场景」",
  "「美妆出海东南亚，需要达人矩阵方案」",
];

const TYPEWRITER_LINES_EN = [
  "Enter brand name to get a 5-min diagnosis report...",
  "「Emerging pet food brand, targeting RED Autumn campaigns」",
  "「Beauty brand expanding to SEA, seeking creator matrix」",
];

/**
 * S5 · CTA（深色 · 橙色线条星球 + 动态打字机搜索栏 + 复合宇宙动效）
 */
export const FinalCtaSection: React.FC<FinalCtaSectionProps> = ({
  onContactClick,
  onExploreProducts,
  onDiagnosisSubmit,
  isNavigating = false,
}) => {
  const t = useT();
  const { lang } = useLang();
  const lines = lang === "zh" ? TYPEWRITER_LINES_ZH : TYPEWRITER_LINES_EN;

  const [displayText, setDisplayText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);

  // 3D 鼠标互动景深视差
  const sectionRef = useRef<HTMLElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { stiffness: 50, damping: 20 };
  const smoothMouseX = useSpring(mouseX, springConfig);
  const smoothMouseY = useSpring(mouseY, springConfig);

  const planetRotateX = useTransform(smoothMouseY, [-0.5, 0.5], [6, -6]);
  const planetRotateY = useTransform(smoothMouseX, [-0.5, 0.5], [-8, 8]);
  const planetTranslateX = useTransform(smoothMouseX, [-0.5, 0.5], [-16, 16]);
  const planetTranslateY = useTransform(smoothMouseY, [-0.5, 0.5], [-12, 12]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // 打字机节奏引擎
  useEffect(() => {
    if (isUserTyping) return;

    let li = 0;
    let ci = 0;
    let del = false;
    let timer: NodeJS.Timeout;

    const tick = () => {
      const currentLine = lines[li] || lines[0];
      setDisplayText(currentLine.slice(0, ci));

      let delay = 64;
      if (!del && ci < currentLine.length) {
        ci++;
      } else if (!del) {
        del = true;
        delay = 2600; // 停留时间
      } else if (ci > 0) {
        ci = Math.max(ci - 2, 0);
        delay = 26; // 回退速度
      } else {
        del = false;
        li = (li + 1) % lines.length;
        delay = 520; // 切换下一句延迟
      }

      timer = setTimeout(tick, delay);
    };

    timer = setTimeout(tick, 300);
    return () => clearTimeout(timer);
  }, [isUserTyping, lines]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const finalVal = inputValue.trim() || displayText;
    onDiagnosisSubmit?.(finalVal);
  };

  return (
    <section
      ref={sectionRef}
      id="cta"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative py-32 sm:py-40 lg:py-44 px-6 sm:px-10 lg:px-16 overflow-hidden border-t border-[#F97316]/20 bg-[#0B0907] [perspective:1000px]"
      style={{
        backgroundImage: "radial-gradient(900px 480px at 72% 50%, rgba(249, 115, 22, 0.09) 0%, transparent 70%)",
      }}
      aria-label={t("Call To Action", "行动召唤")}
    >
      {/* 顶部左右十字标尺星标 */}
      <span className="pointer-events-none absolute left-6 sm:left-10 lg:left-14 top-6 text-[#F97316]/60 font-mono text-base select-none" aria-hidden="true">
        +
      </span>
      <span className="pointer-events-none absolute right-6 sm:right-10 lg:right-14 top-6 text-[#F97316]/60 font-mono text-base select-none" aria-hidden="true">
        +
      </span>

      {/* ================= 右侧复合宇宙行星动效区（精准垂直居中，完整无截断） ================= */}
      <div className="pointer-events-none absolute right-[-4%] sm:right-[0%] lg:right-[4%] top-1/2 -translate-y-1/2 w-[min(54vw,760px)] select-none z-0">
        {/* 3D 鼠标互动景深视差 */}
        <motion.div
          style={{
            rotateX: planetRotateX,
            rotateY: planetRotateY,
            x: planetTranslateX,
            y: planetTranslateY,
            transformStyle: "preserve-3d",
          }}
          className="relative w-full h-full"
        >
          {/* 1. 微重力悬浮呼吸动画容器 */}
          <motion.div
            animate={{
              y: [-8, 8, -8],
              rotate: [-1.2, 1.2, -1.2],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="relative w-full h-full"
          >
            {/* 2. 行星日冕极光呼吸背光 */}
            <motion.div
              animate={{
                opacity: [0.4, 0.75, 0.4],
                scale: [0.96, 1.04, 0.96],
              }}
              transition={{
                duration: 5.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] rounded-full bg-gradient-to-tr from-[#F97316]/25 via-[#FB923C]/35 to-[#FDBA74]/15 blur-[60px] pointer-events-none"
            />

            {/* 3. 主线条行星图片素材 */}
            <img
              src="https://photog-test.oss-accelerate.aliyuncs.com/uploads/public/report/20260827/5920bb14bacb426ea7ffa84016653d0f.png"
              alt="Planet Wireframe"
              className="w-full h-auto object-contain mix-blend-screen opacity-95 [filter:saturate(1.08)_contrast(1.05)] relative z-10"
            />

            {/* 4. 星轨公转微光粒子（沿星环倾斜公转） */}
            <div className="absolute inset-0 z-20 pointer-events-none [transform:rotate(-18deg)]">
              {/* 粒子 1：金橙色主巡航卫星 */}
              <motion.div
                animate={{
                  offsetDistance: ["0%", "100%"],
                }}
                transition={{
                  duration: 18,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  offsetPath: "ellipse(44% 20% at 50% 50%)",
                }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#FDBA74] shadow-[0_0_12px_3px_rgba(249,115,22,0.95)]"
              />

              {/* 粒子 2：极光微脉冲跟随点 */}
              <motion.div
                animate={{
                  offsetDistance: ["50%", "150%"],
                }}
                transition={{
                  duration: 24,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  offsetPath: "ellipse(48% 22% at 50% 50%)",
                }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#F97316] shadow-[0_0_8px_2px_rgba(249,115,22,0.8)]"
              />
            </div>

            {/* 5. 周边散落的宇宙星尘闪烁 */}
            <motion.span
              animate={{ opacity: [0.2, 0.9, 0.2], scale: [0.8, 1.2, 0.8] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-[18%] left-[22%] w-1.5 h-1.5 rounded-full bg-[#FDBA74] shadow-[0_0_6px_#F97316] z-20"
            />
            <motion.span
              animate={{ opacity: [0.3, 0.8, 0.3], scale: [0.9, 1.1, 0.9] }}
              transition={{ duration: 4.5, repeat: Infinity, delay: 1.2, ease: "easeInOut" }}
              className="absolute bottom-[24%] left-[16%] w-2 h-2 rounded-full bg-[#F97316] shadow-[0_0_8px_#F97316] z-20"
            />
            <motion.span
              animate={{ opacity: [0.15, 0.75, 0.15] }}
              transition={{ duration: 3.8, repeat: Infinity, delay: 2.1, ease: "easeInOut" }}
              className="absolute top-[12%] right-[18%] text-[#FDBA74] font-mono text-sm select-none z-20"
            >
              +
            </motion.span>
          </motion.div>
        </motion.div>
      </div>

      {/* ================= 左侧内容主体 ================= */}
      <div className="relative z-10 max-w-[1400px] mx-auto flex flex-col gap-8 sm:gap-9 items-start text-left">
        {/* 大标题 */}
        <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-[64px] font-extrabold tracking-tight text-[#F2ECE5] leading-[1.14]">
          <span>{t("Stop Guessing.", "告别未知，")}</span>
          <br />
          <span className="bg-gradient-to-b from-[#FDBA74] via-[#FDBA74] to-[#F97316] bg-clip-text text-transparent">
            {t("Start Seeing.", "开始预见。")}
          </span>
        </h2>

        {/* 动态打字机搜索栏（Searchbar · 呼吸微光） */}
        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-3 w-full max-w-[540px] p-2 pl-5 sm:pl-6 rounded-full bg-white/[0.05] border border-[#F97316]/50 backdrop-blur-md animate-glowpulse transition-all duration-300 focus-within:border-[#F97316] focus-within:bg-white/[0.08]"
        >
          <div className="relative flex-1 min-w-0 flex items-center min-h-[24px] overflow-hidden">
            {/* 未输入状态下紧随文字的打字机与闪烁光标 */}
            {!inputValue && (
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center text-sm sm:text-[14.5px] text-[#F2ECE5]/60 whitespace-nowrap select-none">
                <span>{displayText}</span>
                <span className="text-[#F97316] animate-pulse font-mono text-base font-bold ml-0.5 leading-none" aria-hidden="true">
                  |
                </span>
              </div>
            )}

            {/* 真实交互输入框 */}
            <input
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setIsUserTyping(true);
              }}
              onBlur={() => {
                if (!inputValue.trim()) setIsUserTyping(false);
              }}
              className="relative z-10 w-full bg-transparent border-none outline-none text-[#F2ECE5] text-sm sm:text-[14.5px]"
            />
          </div>

          <button
            type="submit"
            aria-label={t("Submit brand diagnosis request", "提交品牌诊断请求")}
            className="relative z-10 w-10 sm:w-11 h-10 sm:h-11 rounded-full bg-[#F97316] hover:bg-[#FB923C] text-black flex items-center justify-center shrink-0 transition-transform duration-200 hover:-rotate-12 active:scale-95 shadow-[0_0_16px_rgba(249,115,22,0.4)]"
          >
            <ArrowUp className="w-5 h-5 stroke-[2.5]" />
          </button>
        </form>

        {/* 行动按钮组（严格对齐素材 .btn-solid.sm 与 .btn-line.sm 规范） */}
        <div className="flex items-center gap-3.5 sm:gap-4 flex-wrap">
          <button
            type="button"
            onClick={onContactClick}
            className="group inline-flex items-center justify-center gap-3 px-8 sm:px-[34px] min-h-[48px] rounded-full bg-[#F97316] hover:bg-[#FB923C] text-[#0B0907] font-bold text-[14.5px] leading-tight hover:shadow-[0_0_60px_10px_rgba(249,115,22,0.35)] hover:-translate-y-[3px] transition-all duration-300 ease-out active:scale-[0.98]"
          >
            <span>{t("Contact Us", "联系我们")}</span>
            <span className="text-base leading-none transition-transform duration-300 ease-out group-hover:translate-x-1.5" aria-hidden="true">
              ›
            </span>
          </button>

          <button
            type="button"
            onClick={onExploreProducts}
            disabled={isNavigating}
            className="inline-flex items-center justify-center gap-3 px-8 sm:px-[34px] min-h-[48px] rounded-full bg-transparent border-[1.5px] border-white/30 hover:border-[#F97316] text-[#F2ECE5] hover:text-[#FDBA74] font-medium text-[14.5px] leading-tight transition-all duration-300 ease-out active:scale-[0.98]"
          >
            <span>{isNavigating ? t("Redirecting…", "跳转中…") : t("Explore Products", "探索产品")}</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default FinalCtaSection;
